import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import client from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  ArrowLeft, RefreshCw, LogOut, TrendingUp, TrendingDown, Minus, PauseCircle,
  Users, Activity, Zap, Timer, Radio, Cpu, MessageSquare, Send, Eye, Clock,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid, PieChart, Pie, Cell, Legend,
} from "recharts";
import { toast } from "sonner";

const PRESETS = [
  { key: "today", label: "Today" },
  { key: "7d",    label: "7 Days" },
  { key: "30d",   label: "30 Days" },
  { key: "custom",label: "Custom" },
];

const TABS = [
  { key: "overview",     label: "Overview" },
  { key: "technicians",  label: "Technicians" },
  { key: "stations",     label: "Stations" },
  { key: "pause",        label: "Pause Analysis" },
  { key: "engagement",   label: "Engagement" },
  { key: "forecast",     label: "Forecast" },
];

const REASON_LABEL = {
  MISSING_PART: "Missing Part",
  TROUBLESHOOTING: "Troubleshooting",
  CUSTOMER_REQUEST: "Customer Request",
  BIOS_SOFTWARE: "BIOS / Software",
  BREAK: "Break",
  OTHER: "Other",
};
const REASON_COLOR = {
  MISSING_PART: "#ff6b6b",
  TROUBLESHOOTING: "#ffb454",
  CUSTOMER_REQUEST: "#a17dff",
  BIOS_SOFTWARE: "#00e5ff",
  BREAK: "#00ff9d",
  OTHER: "#8a8a94",
};

// ---------- formatting helpers ----------
const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "0m";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s % 60}s`;
  return `${s}s`;
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Muscat",
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};
const todayMuscat = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Muscat" });

// ---------- shared primitives ----------
function KpiCard({ label, value, icon, hint, delta, accent, testid }) {
  const deltaColor = delta == null ? "rgba(255,255,255,0.4)"
    : delta > 0 ? "#00ff9d" : delta < 0 ? "#ff6b6b" : "rgba(255,255,255,0.4)";
  const DeltaIcon = delta == null ? Minus : delta > 0 ? TrendingUp : delta < 0 ? TrendingDown : Minus;
  return (
    <div
      className="rounded-sm border p-5"
      style={{
        borderColor: accent ? "rgba(0,229,255,0.35)" : "var(--border)",
        background: accent ? "rgba(0,229,255,0.05)" : "var(--surface)",
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="overline">{label}</span>
        <span style={{ color: accent ? "var(--accent)" : "rgba(255,255,255,0.35)" }}>{icon}</span>
      </div>
      <div className="mt-3 font-heading text-3xl md:text-4xl text-white tabular-nums" data-testid={testid ? `${testid}-value` : undefined}>
        {value}
      </div>
      <div className="mt-2 flex items-center gap-1.5 text-[11px]" style={{ color: deltaColor }} data-testid={testid ? `${testid}-delta` : undefined}>
        <DeltaIcon size={12} />
        <span className="tabular-nums">{delta == null ? "—" : `${delta > 0 ? "+" : ""}${delta}%`}</span>
        <span className="text-white/35 ml-1">{hint || "vs previous period"}</span>
      </div>
    </div>
  );
}

function Card({ title, subtitle, right, children, testid, className = "" }) {
  return (
    <div className={`rounded-sm border p-5 ${className}`} style={{ borderColor: "var(--border)", background: "var(--surface)" }} data-testid={testid}>
      {(title || right) && (
        <div className="flex items-baseline justify-between mb-3">
          <div>
            {title && <div className="text-white text-sm font-semibold tracking-tight">{title}</div>}
            {subtitle && <div className="text-white/40 text-[11px] mt-0.5">{subtitle}</div>}
          </div>
          {right}
        </div>
      )}
      {children}
    </div>
  );
}

function EmptyState({ label = "No data in this window." }) {
  return <div className="text-white/40 text-sm py-8 text-center">{label}</div>;
}

// ---------- OVERVIEW TAB ----------
function OverviewTab({ data }) {
  const ov = data?.overview || {};
  const overTime = useMemo(
    () => (ov.over_time || []).map((d) => ({ day: (d.day || "").slice(5), builds: d.builds })),
    [ov.over_time],
  );
  const durSeries = useMemo(
    () => (ov.duration_over_time || []).map((d) => ({ day: (d.day || "").slice(5), avg_min: +(d.avg_active_ms / 60000).toFixed(1) })),
    [ov.duration_over_time],
  );
  const fastest = ov.fastest_ref;
  const slowest = ov.slowest_ref;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="TOTAL BUILDS" value={ov.total_builds ?? 0} icon={<Activity size={16}/>} testid="ov-total-builds" delta={null} hint="all-time"/>
        <KpiCard label="COMPLETED" value={ov.completed_all ?? 0} icon={<TrendingUp size={16}/>} testid="ov-completed" delta={ov.deltas?.completed_pct} hint="in window"/>
        <KpiCard label="WAITING" value={ov.waiting_all ?? 0} icon={<Users size={16}/>} testid="ov-waiting" delta={null} hint="right now"/>
        <KpiCard label="BUILDS TODAY" value={ov.builds_today ?? 0} accent icon={<Zap size={16}/>} testid="ov-today" delta={null} hint="Muscat day"/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="THIS WEEK" value={ov.builds_this_week ?? 0} icon={<Activity size={16}/>} testid="ov-week" delta={null} hint="7 Muscat days"/>
        <KpiCard label="THIS MONTH" value={ov.builds_this_month ?? 0} icon={<Activity size={16}/>} testid="ov-month" delta={null} hint="30 Muscat days"/>
        <KpiCard label="AVG ACTIVE BUILD" value={fmtDuration(ov.avg_active_ms)} icon={<Timer size={16}/>} testid="ov-avg-active" delta={ov.deltas?.avg_active_pct} hint="vs previous period"/>
        <KpiCard label="IN WINDOW" value={ov.in_window_completed ?? 0} icon={<TrendingUp size={16}/>} testid="ov-in-window" delta={null} hint="completed"/>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card title="Fastest Build" subtitle="Shortest active time in window" testid="ov-fastest">
          {fastest ? (
            <div>
              <div className="font-heading text-3xl text-white tabular-nums" style={{ color: "var(--accent)" }}>{fmtDuration(fastest.active_ms)}</div>
              <div className="mt-1 text-white/60 text-sm font-mono">{fastest.order_number}</div>
              <div className="text-white/40 text-xs">{fastest.technician_name}</div>
            </div>
          ) : <EmptyState/>}
        </Card>
        <Card title="Slowest Build" subtitle="Longest active time in window" testid="ov-slowest">
          {slowest ? (
            <div>
              <div className="font-heading text-3xl text-white tabular-nums" style={{ color: "#ff9800" }}>{fmtDuration(slowest.active_ms)}</div>
              <div className="mt-1 text-white/60 text-sm font-mono">{slowest.order_number}</div>
              <div className="text-white/40 text-xs">{slowest.technician_name}</div>
            </div>
          ) : <EmptyState/>}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Builds Over Time" subtitle="Completed per day (Asia/Muscat)" testid="ov-chart-builds">
          {overTime.every((r) => r.builds === 0) ? <EmptyState/> : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={overTime} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
                <Bar dataKey="builds" fill="rgba(0,229,255,0.75)" radius={[3,3,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Average Build Duration" subtitle="Mean active time (minutes)" testid="ov-chart-duration">
          {durSeries.every((r) => r.avg_min === 0) ? <EmptyState/> : (
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={durSeries} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="day" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
                <Line type="monotone" dataKey="avg_min" stroke="rgba(0,229,255,0.85)" strokeWidth={2} dot={{ fill: "rgba(0,229,255,1)", r: 3 }} activeDot={{ r: 4 }}/>
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------- TECHNICIANS TAB ----------
function TechniciansTab({ data }) {
  const rows = data?.technicians || [];
  const chartData = useMemo(
    () => rows.slice(0, 12).map((r) => ({ name: r.technician_name, builds: r.builds, avg_min: +(r.avg_active_ms/60000).toFixed(1) })),
    [rows],
  );
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Builds by Technician" subtitle="Completed in window" testid="tech-chart-builds">
          {chartData.length === 0 ? <EmptyState/> : (
            <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 40)}>
              <BarChart layout="vertical" data={chartData} margin={{ top: 8, right: 12, left: 40, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false}/>
                <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false}/>
                <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.55)" fontSize={12} tickLine={false} axisLine={false} width={120}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
                <Bar dataKey="builds" fill="rgba(0,229,255,0.75)" radius={[0,3,3,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Avg Build Time" subtitle="Mean active minutes per tech" testid="tech-chart-avg">
          {chartData.length === 0 ? <EmptyState/> : (
            <ResponsiveContainer width="100%" height={Math.max(180, chartData.length * 40)}>
              <BarChart layout="vertical" data={chartData} margin={{ top: 8, right: 12, left: 40, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false}/>
                <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="name" stroke="rgba(255,255,255,0.55)" fontSize={12} tickLine={false} axisLine={false} width={120}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
                <Bar dataKey="avg_min" fill="#a17dff" radius={[0,3,3,0]}/>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <Card title="Technician Comparison" subtitle="Full breakdown in this window + all-time buckets" testid="tech-table">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-white/50 overline">Technician</TableHead>
                <TableHead className="text-white/50 overline">Station</TableHead>
                <TableHead className="text-white/50 overline">Builds</TableHead>
                <TableHead className="text-white/50 overline">Avg Active</TableHead>
                <TableHead className="text-white/50 overline">Fastest</TableHead>
                <TableHead className="text-white/50 overline">Today</TableHead>
                <TableHead className="text-white/50 overline">This Month</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.length === 0 && <TableRow className="border-white/10"><TableCell colSpan={7} className="text-center text-white/40 py-8">No completed builds in this window.</TableCell></TableRow>}
              {rows.map((r) => (
                <TableRow key={r.technician_name + r.station_id} className="border-white/10 hover:bg-white/5" data-testid={`tech-row-${r.technician_name.replace(/\W+/g, "-")}`}>
                  <TableCell className="text-white font-medium">{r.technician_name}</TableCell>
                  <TableCell className="text-white/60 font-mono text-xs">{r.station_id || "—"}</TableCell>
                  <TableCell className="text-white tabular-nums">{r.builds}</TableCell>
                  <TableCell className="text-white/80 tabular-nums">{fmtDuration(r.avg_active_ms)}</TableCell>
                  <TableCell className="text-white/80 tabular-nums" style={{ color: "var(--accent)" }}>{fmtDuration(r.fastest_ms)}</TableCell>
                  <TableCell className="text-white/70 tabular-nums">{r.completed_today}</TableCell>
                  <TableCell className="text-white/70 tabular-nums">{r.completed_this_month}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
}

// ---------- STATIONS TAB ----------
function StationTile({ s }) {
  const color = s.status === "LIVE" ? "#00e5ff" : s.status === "PAUSED" ? "#ff9800" : s.status === "WAITING" ? "#a17dff" : "#8a8a94";
  const bg =    s.status === "LIVE" ? "rgba(0,229,255,0.06)" : s.status === "PAUSED" ? "rgba(255,152,0,0.06)" : s.status === "WAITING" ? "rgba(161,125,255,0.06)" : "rgba(255,255,255,0.02)";
  return (
    <div className="rounded-md border p-5" style={{ borderColor: `${color}55`, background: bg }} data-testid={`station-tile-${s.station_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Radio size={14} style={{ color }}/>
          <div className="text-white font-semibold tracking-tight">{s.name || s.station_id}</div>
        </div>
        <span className="text-[10px] font-semibold tracking-[0.22em] px-2 py-1 rounded" style={{ color, background: `${color}18`, border: `1px solid ${color}55` }} data-testid={`station-status-${s.station_id}`}>
          {s.status}
        </span>
      </div>
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40">Technician</div>
      <div className="text-white text-sm">{s.technician_name || "—"}</div>
      {s.order_number && (
        <>
          <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-white/40">Current Order</div>
          <div className="text-white text-sm font-mono">{s.order_number}</div>
          <div className="text-white/50 text-xs">{s.customer_name}</div>
        </>
      )}
      {s.status === "PAUSED" && s.current_pause_reason && (
        <div className="mt-3 text-[10px] tracking-[0.24em] uppercase text-white/40">Pause Reason</div>
      )}
      {s.status === "PAUSED" && s.current_pause_reason && (
        <div className="text-white text-sm">{REASON_LABEL[s.current_pause_reason] || s.current_pause_reason}</div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
        <div>
          <div className="overline">Elapsed</div>
          <div className="tabular-nums text-white/85 mt-0.5">{fmtDuration(s.elapsed_ms)}</div>
        </div>
        <div>
          <div className="overline">Waiting</div>
          <div className="tabular-nums text-white/85 mt-0.5">{s.waiting_count}</div>
        </div>
      </div>
    </div>
  );
}
function StationsTab({ data }) {
  const rows = data?.stations?.by_station || [];
  const live = data?.stations?.live || [];
  return (
    <div className="space-y-6">
      <Card title="Live Station Map" subtitle="Real-time station status — Asia/Muscat" testid="station-map">
        {live.length === 0 ? <EmptyState label="No stations configured yet."/> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {live.map((s) => <StationTile key={s.station_id} s={s}/>)}
          </div>
        )}
      </Card>
      <Card title="Builds by Station" subtitle="Completed in window" testid="station-chart">
        {rows.length === 0 ? <EmptyState/> : (
          <ResponsiveContainer width="100%" height={Math.max(180, rows.length * 60)}>
            <BarChart layout="vertical" data={rows} margin={{ top: 8, right: 12, left: 30, bottom: 0 }}>
              <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false}/>
              <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false}/>
              <YAxis type="category" dataKey="station_id" stroke="rgba(255,255,255,0.55)" fontSize={12} tickLine={false} axisLine={false}/>
              <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
              <Bar dataKey="builds" fill="rgba(0,229,255,0.75)" radius={[0,3,3,0]}/>
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}

// ---------- PAUSE ANALYSIS TAB ----------
function PauseTab({ data }) {
  const pa = data?.pause_analysis || {};
  const countRows = useMemo(() => Object.entries(pa.count_by_reason || {}).map(([k, v]) => ({ reason: k, label: REASON_LABEL[k] || k, count: v })), [pa.count_by_reason]);
  const timeRows = useMemo(() => Object.entries(pa.time_by_reason_ms || {}).map(([k, v]) => ({ reason: k, label: REASON_LABEL[k] || k, minutes: +(v/60000).toFixed(1) })), [pa.time_by_reason_ms]);
  const totalCount = countRows.reduce((a, r) => a + r.count, 0);
  const totalTime  = timeRows.reduce((a, r) => a + r.minutes, 0);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="TOTAL PAUSE TIME" value={fmtDuration(pa.total_pause_ms)} icon={<PauseCircle size={16}/>} testid="pause-total-time" delta={null} hint="all reasons"/>
        <KpiCard label="HOURS LOST" value={`${pa.hours_lost ?? 0}h`} icon={<Clock size={16}/>} testid="pause-hours-lost" delta={null} hint="build time cost"/>
        <KpiCard label="AVG PAUSE" value={fmtDuration(pa.avg_pause_ms)} icon={<Timer size={16}/>} testid="pause-avg" delta={null} hint="per interval"/>
        <KpiCard label="PAUSE INTERVALS" value={pa.total_intervals ?? 0} icon={<PauseCircle size={16}/>} testid="pause-count" delta={null} hint="in window"/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card title="Pause Count by Reason" subtitle={`${totalCount} pauses total`} testid="pause-chart-count">
          {totalCount === 0 ? <EmptyState label="No pauses in this window."/> : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={countRows.filter((r) => r.count > 0)} dataKey="count" nameKey="label" innerRadius={55} outerRadius={90} paddingAngle={2}>
                  {countRows.filter((r) => r.count > 0).map((r) => <Cell key={r.reason} fill={REASON_COLOR[r.reason] || "#8a8a94"}/>)}
                </Pie>
                <Legend wrapperStyle={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Pause Time by Reason" subtitle={`${totalTime.toFixed(1)} min total`} testid="pause-chart-time">
          {totalTime === 0 ? <EmptyState label="No pauses in this window."/> : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart layout="vertical" data={timeRows.filter((r) => r.minutes > 0)} margin={{ top: 8, right: 12, left: 30, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.05)" horizontal={false}/>
                <XAxis type="number" stroke="rgba(255,255,255,0.35)" fontSize={11} tickLine={false} axisLine={false}/>
                <YAxis type="category" dataKey="label" stroke="rgba(255,255,255,0.55)" fontSize={12} tickLine={false} axisLine={false} width={130}/>
                <Tooltip contentStyle={{ background: "#0a0a0d", border: "1px solid rgba(0,229,255,0.25)", borderRadius: 4, color: "#fff" }}/>
                <Bar dataKey="minutes" radius={[0,3,3,0]}>
                  {timeRows.map((r) => <Cell key={r.reason} fill={REASON_COLOR[r.reason] || "#8a8a94"}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>
    </div>
  );
}

// ---------- ENGAGEMENT TAB ----------
function EngagementTab({ data }) {
  const en = data?.engagement || {};
  const rate = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="CUSTOMERS OPENED LIVE" value={en.customers_opened ?? 0} icon={<Eye size={16}/>} testid="eng-opened" delta={null} hint="distinct orders"/>
        <KpiCard label="LIVE OPEN RATE" value={rate(en.open_rate)} icon={<TrendingUp size={16}/>} testid="eng-open-rate" delta={null} hint="opened / started"/>
        <KpiCard label="MESSAGES / BUILD" value={en.messages_per_build ?? 0} icon={<MessageSquare size={16}/>} testid="eng-msg-per-build" delta={null} hint="chat volume"/>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KpiCard label="WHATSAPP SENT" value={en.wa_sent ?? 0} icon={<Send size={16}/>} testid="eng-wa-sent" delta={null} hint="dry-run counts"/>
        <KpiCard label="WHATSAPP LINK OPENED" value={en.wa_link_opened ?? 0} icon={<Eye size={16}/>} testid="eng-wa-opened" delta={null} hint="/api/wl clicks"/>
        <KpiCard label="WA LINK OPEN RATE" value={rate(en.wa_link_open_rate)} icon={<TrendingUp size={16}/>} testid="eng-wa-rate" delta={null} hint="opened / sent"/>
      </div>
      <Card title="Notes" subtitle="How these metrics are derived" testid="eng-notes">
        <ul className="text-white/60 text-[13px] leading-6 space-y-1.5 list-disc pl-5">
          <li><span className="text-white/80">Customers Opened Live View</span> — distinct orders with a <code className="text-white/70">LIVE_VIEW_OPENED</code> event fired by the customer's browser (once per session).</li>
          <li><span className="text-white/80">Live Open Rate</span> — opened orders divided by builds that STARTED in the window.</li>
          <li><span className="text-white/80">Messages per Build</span> — total chat messages / builds started in the window.</li>
          <li><span className="text-white/80">WhatsApp Sent</span> — orders with <code className="text-white/70">whatsapp_status = SENT</code> that started in the window. Currently dry-run.</li>
          <li><span className="text-white/80">WA Link Opened</span> — count of <code className="text-white/70">WHATSAPP_LINK_OPENED</code> events. Requires <code className="text-white/70">PUBLIC_APP_URL</code> configured so short-links are delivered.</li>
        </ul>
      </Card>
    </div>
  );
}

// ---------- FORECAST TAB ----------
function ForecastTab({ data }) {
  const fc = data?.forecast || {};
  if (!fc.eligible) {
    return (
      <Card title="Tomorrow's Forecast" subtitle="Requires historical baseline" testid="forecast-card">
        <div className="py-8 text-center">
          <div className="text-white text-3xl font-heading">Not enough data yet</div>
          <div className="mt-2 text-white/50 text-sm">
            We need at least <span className="text-white">14 completed builds</span> in the last 30 Muscat days
            AND activity on <span className="text-white">5 different days</span>.
          </div>
          <div className="mt-6 inline-grid grid-cols-2 gap-x-8 gap-y-2 text-left">
            <div className="text-white/50 text-xs tracking-[0.22em] uppercase">Completed (30d)</div>
            <div className="text-white tabular-nums" data-testid="fc-completed30">{fc.prev_30d_completed ?? 0} / 14</div>
            <div className="text-white/50 text-xs tracking-[0.22em] uppercase">Active Days</div>
            <div className="text-white tabular-nums" data-testid="fc-activedays">{fc.prev_30d_active_days ?? 0} / 5</div>
          </div>
        </div>
      </Card>
    );
  }
  return (
    <div className="space-y-4">
      <Card title="Tomorrow's Expected Builds" subtitle={`Method: ${fc.method === "same_weekday_avg_last_4_weeks" ? "Same weekday, average of the last 4 occurrences" : "30-day daily average"}`} testid="forecast-card">
        <div className="py-6 text-center">
          <div className="font-heading text-6xl md:text-7xl text-white tabular-nums" style={{ color: "var(--accent)" }} data-testid="forecast-expected">{fc.expected_tomorrow}</div>
          <div className="mt-2 text-white/50 text-sm">expected completed builds</div>
        </div>
        <div className="mt-2 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="rounded-sm border p-4" style={{ borderColor: "var(--border)" }}>
            <div className="overline">Sample Size</div>
            <div className="text-white text-xl mt-1 tabular-nums">{fc.sample_size}</div>
          </div>
          <div className="rounded-sm border p-4" style={{ borderColor: "var(--border)" }}>
            <div className="overline">Completed 30d</div>
            <div className="text-white text-xl mt-1 tabular-nums">{fc.prev_30d_completed}</div>
          </div>
          <div className="rounded-sm border p-4" style={{ borderColor: "var(--border)" }}>
            <div className="overline">Active Days</div>
            <div className="text-white text-xl mt-1 tabular-nums">{fc.prev_30d_active_days}</div>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ---------- MAIN ----------
export default function Reports({ embedded = false }) {
  const { logout, user } = useAuth();
  const [preset, setPreset] = useState("7d");
  const [from, setFrom] = useState(todayMuscat());
  const [to, setTo] = useState(todayMuscat());
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { preset };
      if (preset === "custom") { params.from = from; params.to = to; }
      const { data } = await client.get("/reports/v2/summary", { params });
      setData(data);
    } catch (e) {
      toast.error("Could not load reports");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [preset]);

  // Poll the Live Station Map every 5s when Stations tab is active — cheap, admin-only.
  useEffect(() => {
    if (tab !== "stations") return;
    const id = setInterval(async () => {
      try {
        const { data: live } = await client.get("/reports/v2/live-stations");
        setData((prev) => prev ? { ...prev, stations: { ...(prev.stations || {}), live: live.stations } } : prev);
      } catch { /* silent */ }
    }, 5000);
    return () => clearInterval(id);
  }, [tab]);

  return (
    <div className={embedded ? "" : "min-h-screen"}>
      {!embedded && (
        <div className="sticky top-0 z-20 border-b" style={{ borderColor: "var(--border)", background: "rgba(5,5,5,0.85)", backdropFilter: "blur(14px)" }}>
          <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
            <BrandMark subtitle="REPORTS" small />
            <div className="flex items-center gap-3">
              <Link to="/admin" className="text-white/60 hover:text-white text-sm flex items-center gap-1" data-testid="back-to-admin"><ArrowLeft size={14} /> Build Orders</Link>
              <span className="text-xs text-white/50 hidden md:inline">{user?.email}</span>
              <Button variant="ghost" size="sm" onClick={load} data-testid="reports-refresh" className="text-white/70 hover:text-white"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
              <Button variant="ghost" size="sm" onClick={logout} className="text-white/70 hover:text-white"><LogOut size={14} className="mr-1" /> Sign out</Button>
            </div>
          </div>
        </div>
      )}

      <div className={embedded ? "px-5 md:px-8 py-6 max-w-[1600px]" : "max-w-7xl mx-auto px-6 md:px-10 py-10"}>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] font-semibold text-white/45 uppercase">Analytics · Asia/Muscat · V2</div>
            <h1 className={embedded ? "mt-2 text-2xl md:text-3xl font-semibold text-white tracking-tight" : "font-heading text-4xl md:text-5xl mt-2"} data-testid="reports-heading">Reports</h1>
            <p className="text-white/50 mt-1.5 text-sm font-normal">Executive dashboard for builds, technicians, stations, pauses &amp; engagement.</p>
          </div>
          {embedded && (
            <Button variant="ghost" size="sm" onClick={load} data-testid="reports-refresh" className="text-white/70 hover:text-white font-medium h-9"><RefreshCw size={14} className="mr-1" /> Refresh</Button>
          )}
          <div className="flex items-center gap-2" data-testid="reports-preset-chips">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPreset(p.key)}
                data-testid={`preset-${p.key}`}
                className="h-9 px-4 rounded-sm text-xs font-medium border transition-colors hover:bg-white/5"
                style={preset === p.key
                  ? { borderColor: "var(--accent)", background: "rgba(0,229,255,0.08)", color: "var(--accent)" }
                  : { borderColor: "var(--border)", background: "transparent", color: "rgba(255,255,255,0.6)" }}
              >{p.label}</button>
            ))}
          </div>
        </div>

        {preset === "custom" && (
          <div className="mb-6 flex items-center gap-3 text-sm text-white/60" data-testid="custom-range">
            <label className="flex items-center gap-2">From <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-black border border-white/10 rounded-sm px-2 h-9 text-white" data-testid="date-from"/></label>
            <label className="flex items-center gap-2">To <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-black border border-white/10 rounded-sm px-2 h-9 text-white" data-testid="date-to"/></label>
            <Button size="sm" onClick={load} className="h-9 rounded-sm" style={{ background: "var(--accent)", color: "#000" }} data-testid="apply-custom">Apply</Button>
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6 border-b overflow-x-auto" style={{ borderColor: "var(--border)" }} data-testid="reports-tabs">
          <div className="flex items-center gap-1 min-w-max">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className="h-11 px-4 text-[13px] font-medium tracking-tight transition-colors border-b-2"
                style={tab === t.key
                  ? { color: "var(--accent)", borderColor: "var(--accent)" }
                  : { color: "rgba(255,255,255,0.55)", borderColor: "transparent" }}
              >{t.label}</button>
            ))}
          </div>
        </div>

        {loading && !data && <div className="text-white/40 py-16 text-center">Loading reports…</div>}
        {data && (
          <>
            {tab === "overview"    && <OverviewTab data={data}/>}
            {tab === "technicians" && <TechniciansTab data={data}/>}
            {tab === "stations"    && <StationsTab data={data}/>}
            {tab === "pause"       && <PauseTab data={data}/>}
            {tab === "engagement"  && <EngagementTab data={data}/>}
            {tab === "forecast"    && <ForecastTab data={data}/>}
          </>
        )}
      </div>
    </div>
  );
}
